import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Shield, Truck, Award, Users } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import ProductCard from '../components/ProductCard';
import { productsAPI } from '../api';

const Home = () => {
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const products = await productsAPI.getAll();
        const featured = products.filter(p => p.featured).slice(0, 4);
        setFeaturedProducts(featured);
      } catch (error) {
        console.error('Error fetching products:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Professional Hero Section */}
      <section className="relative bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white overflow-hidden">
        {/* Professional banner with overlay */}
        <div className="relative w-full h-[200px] md:h-[240px] lg:h-[280px]">
          <img
            src="https://customer-assets.emergentagent.com/job_reflective-ppe/artifacts/eoxs4mis_Home%20page.png"
            alt="Hi-Viz Pro Kenya - Professional Safety Wear Solutions"
            className="w-full h-full object-cover object-center opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-900/95 via-slate-900/70 to-slate-900/40"></div>
        </div>

        {/* Hero Content */}
        <div className="absolute inset-0 flex items-center">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
            <div className="max-w-3xl">
              <div className="inline-flex items-center space-x-2 bg-orange-600 px-3 py-1.5 rounded-full mb-4">
                <Shield className="h-4 w-4" />
                <span className="text-xs font-semibold tracking-wide">DOSHS & KEBS CERTIFIED</span>
              </div>
              
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold mb-3 leading-tight">
                Professional Safety Wear Solutions
              </h1>
              
              <p className="text-sm md:text-base text-slate-200 mb-6 leading-relaxed">
                Certified high-visibility PPE for construction, engineering, and industrial sectors across Kenya
              </p>
              
              <div className="flex flex-wrap gap-3">
                <Link to="/products">
                  <Button size="lg" className="bg-orange-600 hover:bg-orange-700 text-white font-semibold px-6">
                    Shop Products
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
                <Link to="/contact">
                  <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-slate-900 font-semibold px-6">
                    Request Quote
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Indicators - Enhanced */}
      <section className="bg-white py-12 border-b border-gray-100 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="text-center group">
              <div className="inline-flex items-center justify-center w-14 h-14 bg-orange-50 rounded-2xl mb-3 group-hover:bg-orange-100 transition-colors">
                <Shield className="h-7 w-7 text-orange-600" />
              </div>
              <h3 className="font-bold text-gray-900 mb-1 text-sm">Certified Products</h3>
              <p className="text-xs text-gray-600">DOSHS & KEBS Compliant</p>
            </div>
            
            <div className="text-center group">
              <div className="inline-flex items-center justify-center w-14 h-14 bg-blue-50 rounded-2xl mb-3 group-hover:bg-blue-100 transition-colors">
                <Truck className="h-7 w-7 text-blue-600" />
              </div>
              <h3 className="font-bold text-gray-900 mb-1 text-sm">Fast Delivery</h3>
              <p className="text-xs text-gray-600">Nationwide Shipping</p>
            </div>
            
            <div className="text-center group">
              <div className="inline-flex items-center justify-center w-14 h-14 bg-green-50 rounded-2xl mb-3 group-hover:bg-green-100 transition-colors">
                <Award className="h-7 w-7 text-green-600" />
              </div>
              <h3 className="font-bold text-gray-900 mb-1 text-sm">Quality Assured</h3>
              <p className="text-xs text-gray-600">ISO Standards Met</p>
            </div>
            
            <div className="text-center group">
              <div className="inline-flex items-center justify-center w-14 h-14 bg-purple-50 rounded-2xl mb-3 group-hover:bg-purple-100 transition-colors">
                <Users className="h-7 w-7 text-purple-600" />
              </div>
              <h3 className="font-bold text-gray-900 mb-1 text-sm">Bulk Discounts</h3>
              <p className="text-xs text-gray-600">Save Up to 15%</p>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Products - Enhanced */}
      <section className="py-20 bg-gradient-to-b from-white to-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="inline-block bg-orange-100 text-orange-600 px-3 py-1 rounded-full text-sm font-semibold mb-4">
              SHOP NOW
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Featured Safety Equipment
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Certified PPE with bulk discounts for construction, engineering & industrial sectors
            </p>
          </div>
          
          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600"></div>
              <p className="text-gray-600 mt-4">Loading products...</p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
                {featuredProducts.map(product => (
                  <ProductCard key={product.id} product={product} />
                ))}
              </div>
              
              <div className="text-center">
                <Link to="/products">
                  <Button size="lg" className="bg-orange-600 hover:bg-orange-700 text-white font-semibold px-8">
                    View All Products
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
              </div>
            </>
          )}
        </div>
      </section>

      {/* Bulk Discount Highlight - Professional */}
      <section className="py-20 bg-gradient-to-br from-orange-50 via-white to-blue-50 border-y border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="inline-block bg-green-100 text-green-700 px-3 py-1 rounded-full text-sm font-semibold mb-4">
              SAVE MORE
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Bulk Order Discounts
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Progressive savings for contractors and businesses - the more you buy, the more you save
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            <Card className="border-2 border-green-100 hover:border-green-300 transition-all hover:shadow-lg">
              <CardContent className="pt-8 pb-8 text-center">
                <div className="text-5xl font-bold text-green-600 mb-3">5%</div>
                <p className="text-xl font-bold text-gray-900 mb-2">10-49 Items</p>
                <p className="text-sm text-gray-600">Starter pack savings</p>
              </CardContent>
            </Card>
            
            <Card className="border-2 border-orange-300 shadow-xl scale-105 relative overflow-hidden">
              <div className="absolute top-0 right-0 bg-orange-600 text-white px-3 py-1 text-xs font-bold">
                POPULAR
              </div>
              <CardContent className="pt-8 pb-8 text-center">
                <div className="text-5xl font-bold text-orange-600 mb-3">10%</div>
                <p className="text-xl font-bold text-gray-900 mb-2">50-99 Items</p>
                <p className="text-sm text-gray-600">Mid-size project discount</p>
              </CardContent>
            </Card>
            
            <Card className="border-2 border-blue-100 hover:border-blue-300 transition-all hover:shadow-lg">
              <CardContent className="pt-8 pb-8 text-center">
                <div className="text-5xl font-bold text-blue-600 mb-3">15%</div>
                <p className="text-xl font-bold text-gray-900 mb-2">100+ Items</p>
                <p className="text-sm text-gray-600">Enterprise savings</p>
              </CardContent>
            </Card>
          </div>
          
          <div className="text-center mt-10">
            <Link to="/contact">
              <Button size="lg" variant="outline" className="border-2 border-orange-600 text-orange-600 hover:bg-orange-600 hover:text-white font-semibold px-8">
                Request Bulk Quote
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Compliance Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Certified & Compliant Safety Solutions
              </h2>
              <p className="text-lg text-gray-600 mb-6">
                All our products meet Kenya's Occupational Safety and Health Act (OSHA) requirements 
                and comply with international standards.
              </p>
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-green-600 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-white text-xs font-bold">✓</span>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">DOSHS Compliant</h3>
                    <p className="text-sm text-gray-600">Directorate of Occupational Safety and Health Services approved</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-green-600 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-white text-xs font-bold">✓</span>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">KEBS Certified</h3>
                    <p className="text-sm text-gray-600">Kenya Bureau of Standards quality assurance</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-green-600 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-white text-xs font-bold">✓</span>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">EN ISO 20471 Standards</h3>
                    <p className="text-sm text-gray-600">International high-visibility warning clothing standards</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-green-600 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-white text-xs font-bold">✓</span>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">ANSI/ISO Compliant</h3>
                    <p className="text-sm text-gray-600">American National Standards Institute approved</p>
                  </div>
                </div>
              </div>
            </div>
            
            <Card className="border-2 border-orange-200">
              <CardContent className="p-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Industries We Serve</h3>
                <div className="space-y-3">
                  {[
                    'Construction & Civil Engineering',
                    'Manufacturing & Industrial Plants',
                    'Oil, Gas & Energy Sectors',
                    'Transportation & Logistics',
                    'NGOs & Humanitarian Organizations',
                    'Government Departments & Institutions'
                  ].map((industry, index) => (
                    <div key={index} className="flex items-center space-x-3">
                      <ArrowRight className="h-5 w-5 text-orange-600 flex-shrink-0" />
                      <span className="text-gray-700">{industry}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-gradient-to-r from-orange-600 to-orange-700 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Ready to Order Safety Equipment?
          </h2>
          <p className="text-xl text-orange-100 mb-8 max-w-2xl mx-auto">
            Contact us for bulk orders, custom branding, or project-specific requirements
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/products">
              <Button size="lg" className="bg-white text-orange-600 hover:bg-gray-100">
                Browse Products
              </Button>
            </Link>
            <a href="https://wa.me/254716504759" target="_blank" rel="noopener noreferrer">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-orange-600">
                WhatsApp Us
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
